import React, { useState, useEffect } from 'react';
import { FUEL_TYPES } from '../../mock/mock';
import { Calendar, Loader2, CloudOff, RefreshCw, Camera } from 'lucide-react';
import { api, photoURL } from '../../lib/api';
import { PhotoZoomSheet } from '../../components/PhotoZoomSheet';
import { getQueue, getDraftsCount } from '../../lib/offline';
import { syncQueue, onSyncChange } from '../../lib/sync';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import TrackingCompliance from './TrackingCompliance';

const DraftsLink = () => {
  const navigate = useNavigate();
  const [n, setN] = useState(0);
  useEffect(() => {
    const r = () => getDraftsCount().then(setN).catch(() => {});
    r();
    window.addEventListener('drafts-changed', r);
    return () => window.removeEventListener('drafts-changed', r);
  }, []);
  if (!n) return null;
  return (
    <button onClick={() => navigate('/app/drafts')} className="text-[11px] text-[#0f1d6f] font-medium underline underline-offset-4" data-testid="history-drafts-link">
      Por enviar ({n})
    </button>
  );
};

const History = () => {
  const { user } = useAuth();
  const [filter, setFilter] = useState('all');
  const [entries, setEntries] = useState([]);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [photoView, setPhotoView] = useState(null);

  const load = async () => {
    const [server, q] = await Promise.all([
      api.get('/prices', { params: { mine: true } }).then(r => r.data).catch(() => []),
      getQueue(),
    ]);
    setEntries(server);
    setQueue(q);
    setLoading(false);
  };

  useEffect(() => {
    load();
    const off = onSyncChange((s) => {
      setSyncing(!!s.syncing);
      if (!s.syncing) load();
    });
    return off;
  }, []);

  const queueAsEntries = queue.map(q => ({
    id: 'q' + q.id, station_name: q.stationName, brand: q.brand,
    captured_at: q.capturedAt || q.createdAt, prices: q.prices,
    notes: q.notes, status: 'offline', _offline: true,
  }));

  // Regla: el histórico del capturador solo muestra los últimos 7 días
  const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const allEntries = [...queueAsEntries, ...entries]
    .filter(e => !e.captured_at || new Date(e.captured_at).getTime() >= weekAgo);
  const filtered = filter === 'all' ? allEntries
    : filter === 'anomaly' ? allEntries.filter(p => p.gps_anomaly)
    : filter === 'tracked' ? allEntries.filter(p => p.status === 'tracked' || p.status === 'approved')
    : allEntries.filter(p => p.status === filter);

  // TMs y Agentes de estación: MO-P07 muestra el cumplimiento de trackings
  // diarios por estación (igual que el TM), con meta según horario AD-P06.
  if (user?.role === 'territory_manager' || user?.role === 'agent') return <TrackingCompliance />;

  return (
    <div className="px-5 py-5">
      <div className="flex items-end justify-between mb-1">
        <h2 className="font-serif text-[28px] leading-tight tracking-tight">Mis capturas</h2>
        <div className="flex items-center gap-3">
          <DraftsLink />
          {queue.length > 0 && (
          <button onClick={() => syncQueue()} disabled={syncing || !navigator.onLine} className="text-[11px] text-[#1c1c1c] underline underline-offset-4 inline-flex items-center gap-1 disabled:opacity-50">
            <RefreshCw size={11} className={syncing ? 'animate-spin' : ''}/> Sincronizar
          </button>
          )}
        </div>
      </div>
      <p className="text-[13px] text-[#5a5a5a] mb-5">
        {allEntries.filter(e => !e._offline).length} enviadas · últimos 7 días {queue.length > 0 && <span className="text-[#92400e]">· {queue.length} en cola</span>}
      </p>

      <div className="flex gap-2 mb-5 overflow-x-auto pb-1 -mx-4 px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="history-filter-chips">
        {[['all','Todas'],['offline','En cola'],['tracked','Registradas'],['anomaly','Anomalías']].map(([k,l]) => (
          <button key={k} onClick={() => setFilter(k)} className={`press px-3.5 h-8 text-[12px] rounded-full border shrink-0 whitespace-nowrap transition-colors duration-150 ${filter===k?'bg-[#1c1c1c] text-[#f8f6f1] border-[#1c1c1c]':'bg-white border-[#e8e5de] text-[#5a5a5a]'}`}>{l}</button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#8a8579]"/></div> : (
        <div className="space-y-3">
          {filtered.map(e => (
            <div key={e.id} className={`bg-white border rounded-xl p-4 ${e._offline ? 'border-[#fde68a] bg-[#fffbeb]' : 'border-[#e8e5de]'}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[10px] tracking-wider uppercase text-[#8a8579]">{e.brand}</p>
                  <h4 className="text-[14px] font-medium tracking-tight">{e.station_name}</h4>
                  <div className="flex items-center gap-1.5 text-[11px] text-[#8a8579] mt-1"><Calendar size={11}/>{new Date(e.captured_at).toLocaleString('es-GT', { dateStyle: 'medium', timeStyle: 'short' })}</div>
                </div>
                <span className={`text-[10px] px-2 py-1 rounded-full inline-flex items-center gap-1 ${
                  e.gps_anomaly?'bg-[#fee2e2] text-[#991b1b]':
                  e.status==='rejected'?'bg-[#e8e5de] text-[#5a5a5a]':
                  e.status==='offline'?'bg-[#fef3c7] text-[#92400e]':
                  'bg-[#1c1c1c] text-[#f8f6f1]'}`}>
                  {e.status==='offline' && <CloudOff size={9}/>}
                  {e.status==='offline'?'En cola':e.status==='rejected'?'Rechazada':e.gps_anomaly?'Anomalía':'Registrada'}
                </span>
              </div>
              <div className="mt-3 grid grid-cols-4 gap-2">
                {FUEL_TYPES.map(ft => (
                  <div key={ft.id} className="bg-[#f3f0e9] rounded-md px-2 py-1.5">
                    <p className="text-[9px] uppercase tracking-wider text-[#8a8579]">{ft.name}</p>
                    <p className="font-mono text-[12px] tabular-nums">{e.prices?.[ft.id] != null ? `Q${Number(e.prices[ft.id]).toFixed(2)}` : '—'}</p>
                  </div>
                ))}
              </div>
              {e.notes && <p className="text-[11px] text-[#5a5a5a] mt-2 italic">{e.notes}</p>}
              {!e._offline && e.photo_url && (
                <button
                  onClick={() => setPhotoView({ src: photoURL(e.photo_url), title: `${e.station_name} · ${new Date(e.captured_at).toLocaleDateString('es-GT')}` })}
                  className="press mt-2.5 inline-flex items-center gap-1.5 text-[11.5px] text-[#0f1d6f] underline underline-offset-2"
                  data-testid={`history-photo-${e.id}`}
                >
                  <Camera size={12}/> Ver foto
                </button>
              )}
            </div>
          ))}
          {filtered.length === 0 && <p className="text-center text-[13px] text-[#8a8579] py-12">Sin capturas</p>}
        </div>
      )}
      <PhotoZoomSheet open={!!photoView} onOpenChange={(o) => { if (!o) setPhotoView(null); }} src={photoView?.src} title={photoView?.title}/>
    </div>
  );
};

export default History;
